import { type Key } from 'swr'
import useSWRMutation, { type SWRMutationResponse } from 'swr/mutation'

import {
  type CartControllerAddResponse,
  CartControllerV4ApiFactory,
  type PrescriptionPrescriptionTypeEnum,
  type RxByPrincipleSelectableReason,
} from '@redteclab/api/clients/bully'

import { useBullyApi } from '../../../base-api-communication'
import { useGlobalConfigContext } from '../../../global-config'
import { type CartIntermediateEntryObject } from '../../model/CartIntermediateContextValue'
import { buildRxByPrincipleParams } from '../buildRxByPrincipleParams'

const getCartIntermediateApiSwrProductAddToCartKey = (): Key => {
  return 'cartIntermediateApiSwrProductAddToCart'
}

export type CartIntermediateMultipleProductsAddToCartProps = {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  products: CartIntermediateEntryObject[]
  rxByPrincipleSelectedReason?: RxByPrincipleSelectableReason
}

export const useCartIntermediateApiSwrMultipleProductsAddToCartMutation = ({
  currentPrescriptionType,
  products,
  rxByPrincipleSelectedReason,
}: {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  products: CartIntermediateEntryObject[]
  rxByPrincipleSelectedReason?: RxByPrincipleSelectableReason
}): SWRMutationResponse<CartControllerAddResponse[] | null> => {
  const config = useGlobalConfigContext()
  const client = useBullyApi(CartControllerV4ApiFactory)

  const fetcher = async (): Promise<CartControllerAddResponse[] | null> => {
    const addToCartRequestBody = products.map((product) => ({
      offerId: product.id,
      prescriptionType: currentPrescriptionType,
      quantity: 1,
      rxByPrinciple: buildRxByPrincipleParams(rxByPrincipleSelectedReason),
    }))

    const payload = {
      Accept_Language: config.locale,
      CartV2CartEntriesRequestV1: {
        listOfCartEntries: [...addToCartRequestBody],
      },
      language: config.language,
      tenant: config.tenantAndEnv,
    }
    const response =
      await client.cartServiceCartPostAddToCartEntriesLocalized(payload)

    return response.data
  }

  return useSWRMutation(getCartIntermediateApiSwrProductAddToCartKey(), fetcher)
}
